import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { RolloutPolicyBadge } from "@/components/RolloutPolicyBadge";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type { FeatureCatalogEntry, ProviderCatalogEntry, RolloutPolicy } from "@tilt/admin-sdk";

/**
 * /platform/catalog/features/:path - per-feature detail page (V0089).
 *
 * Shows:
 *  - Feature metadata + whole-feature rollout_policy toggle (super_admin only)
 *  - Provider support table: which providers serve this feature, each pair's
 *    rollout_policy, and the AND-merged effective policy
 *  - Guidance to manage per-scope decisions via Partner/Org/Location detail pages
 */
export function FeatureDetailPage(): JSX.Element {
  const { path: featurePath = "" } = useParams<{ path: string }>();
  usePageTitle(`Feature: ${featurePath}`);

  const qc = useQueryClient();
  const isSuper = isSuperAdmin(useRoleProfile());

  const catalogQ = useQuery({
    queryKey: ["feature-catalog"],
    queryFn: () => apiClient.listFeatureCatalog(),
    staleTime: 30_000,
  });
  const feature: FeatureCatalogEntry | undefined = catalogQ.data?.features.find(
    (f) => f.path === featurePath,
  );

  const providersQ = useQuery({
    queryKey: ["provider-catalog"],
    queryFn: () => apiClient.listProviderCatalog(),
    staleTime: 30_000,
  });

  if (catalogQ.isLoading || providersQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!feature) {
    return (
      <div className="space-y-3">
        <BackLink />
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Feature <code className="font-mono">{featurePath}</code> not found. It may not be seeded
          yet or the migration hasn&apos;t applied.
        </div>
      </div>
    );
  }

  const supportingProviders: Array<{
    provider: ProviderCatalogEntry;
    pairPolicy: RolloutPolicy;
    effectivePolicy: RolloutPolicy;
  }> = (providersQ.data?.providers ?? [])
    .filter((p) => p.supported_feature_paths.includes(featurePath))
    .map((p) => {
      const sup = (p.supported_features ?? []).find((s) => s.feature_path === featurePath);
      const pairPolicy: RolloutPolicy = sup?.rollout_policy ?? "broadcast";
      const featurePolicy: RolloutPolicy = feature.rollout_policy ?? "broadcast";
      const effectivePolicy: RolloutPolicy =
        featurePolicy === "controlled" || pairPolicy === "controlled" ? "controlled" : "broadcast";
      return { provider: p, pairPolicy, effectivePolicy };
    });

  return (
    <div className="space-y-5">
      <BackLink />

      <FeatureHeader
        feature={feature}
        isSuper={isSuper}
        onMutated={() => {
          void qc.invalidateQueries({ queryKey: ["feature-catalog"] });
        }}
      />

      <ProviderSupportTable
        featurePath={featurePath}
        supportingProviders={supportingProviders}
        isSuper={isSuper}
        onMutated={() => {
          void qc.invalidateQueries({ queryKey: ["provider-catalog"] });
        }}
      />

      <section className="rounded-md border bg-muted/30 p-4 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">Per-scope decisions</p>
        <p className="mt-1">
          Enable or disable this feature for specific partners, organizations, or locations from
          their detail pages (Applications tab). The rollout policy above is the platform-level
          default; scope-level decisions override it.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Link
            to="/partners"
            className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            Partners →
          </Link>
          <Link
            to="/organizations"
            className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            Organizations →
          </Link>
          <Link
            to="/locations"
            className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            Locations →
          </Link>
        </div>
      </section>
    </div>
  );
}

function BackLink(): JSX.Element {
  return (
    <Link
      to="/platform/catalog?tab=features"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Platform catalog - Features
    </Link>
  );
}

function FeatureHeader(props: {
  feature: FeatureCatalogEntry;
  isSuper: boolean;
  onMutated: () => void;
}): JSX.Element {
  const { feature } = props;
  const [error, setError] = useState<string | null>(null);

  const patchRollout = useMutation({
    mutationFn: (policy: RolloutPolicy) =>
      apiClient.patchFeatureRollout(feature.path, { rollout_policy: policy }),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (e: unknown) => setError(describe(e)),
  });

  const currentPolicy: RolloutPolicy = feature.rollout_policy ?? "broadcast";
  const nextPolicy: RolloutPolicy = currentPolicy === "broadcast" ? "controlled" : "broadcast";

  return (
    <section className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {feature.is_active ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
            ) : (
              <XCircle className="h-5 w-5 text-muted-foreground" aria-hidden />
            )}
            <h1 className="text-lg font-semibold">{feature.display_name}</h1>
            <RolloutPolicyBadge policy={currentPolicy} />
            {feature.is_provider_bindable && (
              <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-800 dark:bg-sky-900/30 dark:text-sky-200">
                Provider-bindable
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">{feature.path}</div>
          {feature.description && (
            <p className="mt-1.5 text-sm text-muted-foreground">{feature.description}</p>
          )}
        </div>

        {props.isSuper && (
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              disabled={patchRollout.isPending}
              onClick={() => patchRollout.mutate(nextPolicy)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50",
                currentPolicy === "broadcast"
                  ? "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300"
                  : "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300",
              )}
            >
              {patchRollout.isPending
                ? "Saving…"
                : currentPolicy === "broadcast"
                  ? "Set to Pilot (controlled)"
                  : "Set to Live (broadcast)"}
            </button>
            <p className="text-[10px] text-muted-foreground">
              {currentPolicy === "broadcast"
                ? "Currently live - on for all scopes unless opted out"
                : "Currently pilot - off everywhere unless explicitly granted"}
            </p>
          </div>
        )}
      </div>
      {error && (
        <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </div>
      )}
    </section>
  );
}

function ProviderSupportTable(props: {
  featurePath: string;
  supportingProviders: Array<{
    provider: ProviderCatalogEntry;
    pairPolicy: RolloutPolicy;
    effectivePolicy: RolloutPolicy;
  }>;
  isSuper: boolean;
  onMutated: () => void;
}): JSX.Element {
  const { featurePath, supportingProviders } = props;
  const [errors, setErrors] = useState<Record<string, string>>({});

  const patchPair = useMutation({
    mutationFn: ({
      providerId,
      policy,
    }: {
      providerId: string;
      providerSlug: string;
      policy: RolloutPolicy;
    }) =>
      apiClient.patchProviderFeatureRollout(providerId, featurePath, {
        rollout_policy: policy,
      }),
    onSuccess: (_data, vars) => {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[vars.providerSlug];
        return next;
      });
      props.onMutated();
    },
    onError: (e: unknown, vars) => {
      setErrors((prev) => ({ ...prev, [vars.providerSlug]: describe(e) }));
    },
  });

  return (
    <section className="space-y-2">
      <div>
        <p className="text-sm font-medium">Provider support</p>
        <p className="text-xs text-muted-foreground">
          Providers that can deliver this feature. Effective policy = AND-merge of the whole-feature
          rollout (above) and the per-(provider, feature) pair policy. Both must be{" "}
          <span className="font-medium text-emerald-700 dark:text-emerald-300">Live</span> for the
          feature to be live end-to-end through that provider.
        </p>
      </div>

      {supportingProviders.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/40 p-4 text-center text-sm text-muted-foreground">
          No providers support this feature yet. Add this feature to a provider via the Providers
          tab → Edit → Supported features.
        </div>
      ) : (
        <ul className="divide-y rounded-md border bg-card">
          {supportingProviders.map(({ provider, pairPolicy, effectivePolicy }) => {
            const nextPairPolicy: RolloutPolicy =
              pairPolicy === "broadcast" ? "controlled" : "broadcast";
            return (
              <li key={provider.slug} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{provider.display_name}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {provider.slug}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <span>
                      Pair policy: <RolloutPolicyBadge policy={pairPolicy} />
                    </span>
                    <span>→</span>
                    <span>
                      Effective: <RolloutPolicyBadge policy={effectivePolicy} />
                    </span>
                  </div>
                </div>

                {props.isSuper && (
                  <button
                    type="button"
                    disabled={patchPair.isPending}
                    onClick={() =>
                      patchPair.mutate({
                        providerId: provider.id,
                        providerSlug: provider.slug,
                        policy: nextPairPolicy,
                      })
                    }
                    className={cn(
                      "rounded-md border px-2 py-1 text-[11px] disabled:opacity-50",
                      pairPolicy === "broadcast"
                        ? "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300"
                        : "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300",
                    )}
                  >
                    {pairPolicy === "broadcast" ? "Set Pilot" : "Set Live"}
                  </button>
                )}

                {errors[provider.slug] && (
                  <p className="w-full text-[11px] text-destructive">{errors[provider.slug]}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
